import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Storage } from 'aws-amplify';
import { Tooltip, withStyles } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';

import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import AttachFileIcon from '@/component/common/icons/AttachFileIcon';
import DownloadFileIcon from '@/component/common/icons/DownloadFileIcon';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';
import trI18n from '@/common/utils/trI18n';
import tr from '@/common/utils/tr';
import useTranslationSubscription from '@/hooks/useTranslation';

const FlagMessageCopyLink = withStyles({
    tooltip: {
        color: '#1DA578',
        backgroundColor: '#D7F4E2',
        padding: '8px 16px',
        fontSize: '14px',
        boxShadow: '6px 5px 5px -4px rgba(0,0,0,0.3)'
    },
    arrow: {
        color: '#D7F4E2'
    }
})(Tooltip);

function ViewFile({ messageData }) {
    useTranslationSubscription();
    
    const [fileData, setFileData] = useState({ fileName: '', url: '', s3Key: '' });
    const [isShowFlagMessage, setIsShowFlagMessage] = useState(false);

    useEffect(() => {
        const fetchFileUrl = async () => {
            if (!messageData?.content) return;

            try {
                const key = messageData.content.replace(/^public\//, '');
                const url = await Storage.get(key, { expires: Constant.AI_PRESIGNED_URL_EXPIRE_TIME });
                
                // Extract filename from s3 key and try to get original name (remove UUID suffix)
                let fileName = key.split('/').pop();
                // Pattern: {originalName}_{uuid}.{ext} - try to extract originalName
                const uuidPattern = /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\.[^.]+)$/i;
                if (uuidPattern.test(fileName)) {
                    fileName = fileName.replace(uuidPattern, '$1');
                }

                setFileData({ fileName, url, s3Key: key });
            } catch (err) {
                console.error('ViewFile fetchFileUrl err:', err);
            }
        };

        fetchFileUrl();
    }, [messageData]);

    const fileExtension = useMemo(() => {
        return Utils.getFileExtension(fileData.fileName) || '';
    }, [fileData.fileName]);

    const downloadFile = useCallback(async () => {
        try {
            const response = await fetch(fileData.url, { mode: 'cors' });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileData.fileName);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('downloadFile error:', error);
        }
    }, [fileData]);

    const copyLinkFile = useCallback(async () => {
        try {
            // Get fresh presigned URL from S3 (same as ViewDocument.js)
            const s3Key = messageData?.content?.replace(/^public\//, '') || fileData.s3Key;
            const presignedUrl = await Storage.get(s3Key, { 
                level: 'public',
                expires: Constant.AI_PRESIGNED_URL_EXPIRE_TIME 
            });
            
            await navigator.clipboard.writeText(presignedUrl);

            setIsShowFlagMessage(true);
            setTimeout(() => {
                setIsShowFlagMessage(false);
            }, 1000);
        } catch (err) {
            console.error('copyLinkFile err:', err);
        }
    }, [messageData, fileData.s3Key]);

    if (!messageData?.content) return null;

    return (
        <div className="ai-preview-upload-item ai-preview-upload-file-item ai-view-document-item">
            <div className="ai-preview-file-icon">
                <AttachFileIcon extension={fileExtension} background="#FFFFFF" />
            </div>
            <div className="ai-preview-file-info">
                <OverflowTooltipContent
                    text={fileData?.fileName || tr('File')}
                    className="ai-preview-file-name"
                    style={{ maxWidth: '200px' }}
                    placement='bottom'
                />
            </div>
            <div className="ai-view-document-options">
                <Tooltip title={trI18n('DOWNLOAD')} placement="top" arrow>
                    <button
                        className="btn-reset-default document-option-item"
                        onClick={downloadFile}
                    >
                        <DownloadFileIcon />
                    </button>
                </Tooltip>
                <Tooltip title={trI18n('COPY_URL')} placement="top" arrow>
                    <button
                        className="btn-reset-default document-option-item"
                        onClick={copyLinkFile}
                    >
                        <FlagMessageCopyLink
                            title={trI18n('KNOWDEMSG002')}
                            placement="top-start"
                            arrow={true}
                            open={isShowFlagMessage}
                        >
                            <LinkIcon className="ai-doc-copy-link-icon" />
                        </FlagMessageCopyLink>
                    </button>
                </Tooltip>
            </div>
        </div>
    );
}

ViewFile.propTypes = {
    messageData: PropTypes.any
};

export default ViewFile;
